import React, { type ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import EditorDateIcon from '@atlaskit/icon/core/calendar';
import Lozenge, { type SemanticColor } from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const lozengeStyleMapping = {
	onTrack: {
		default: {
			backgroundColor: `${token('color.background.accent.green.subtle')}`,
			color: `${token('color.text')}`,
		},
		withScore: {
			backgroundColor: `${token('color.background.accent.lime.subtler')}`,
			secondaryBackgroundColor: `${token('color.background.accent.lime.subtle')}`,
			color: `${token('color.text')}`,
		},
	},
	atRisk: {
		default: {
			backgroundColor: `${token('color.background.accent.yellow.subtle')}`,
			color: `${token('color.text')}`,
		},
		withScore: {
			backgroundColor: `${token('color.background.accent.yellow.subtler')}`,
			secondaryBackgroundColor: `${token('color.background.accent.yellow.subtle')}`,
			color: `${token('color.text')}`,
		},
	},
	offTrack: {
		default: {
			backgroundColor: `${token('color.background.accent.red.subtle')}`,
			color: `${token('color.text')}`,
		},
		withScore: {
			backgroundColor: `${token('color.background.accent.red.subtle')}`,
			secondaryBackgroundColor: `${token('color.border.accent.red')}`,
			color: `${token('color.text')}`,
		},
	},
	date: {
		default: {
			backgroundColor: `${token('color.background.neutral.subtle')}`,
			color: `${token('color.text')}`,
		},
	},
	default: {
		default: {
			backgroundColor: `${token('color.background.accent.gray.subtler')}`,
			color: `${token('color.text')}`,
		},
		withScore: {
			backgroundColor: `${token('color.background.neutral')}`,
			secondaryBackgroundColor: `${token('color.background.inverse.subtle')}`,
			color: `${token('color.text')}`,
		},
	},
};

type LozengeStyle = {
	backgroundColor: string;
	secondaryBackgroundColor?: string;
	color: string;
};

const getStatusLozengeStyle = (statusType: string, hasScore: boolean): LozengeStyle => {
	switch (statusType) {
		case 'on_track':
			return hasScore ? lozengeStyleMapping.onTrack.withScore : lozengeStyleMapping.onTrack.default;
		case 'at_risk':
			return hasScore ? lozengeStyleMapping.atRisk.withScore : lozengeStyleMapping.atRisk.default;
		case 'off_track':
			return hasScore
				? lozengeStyleMapping.offTrack.withScore
				: lozengeStyleMapping.offTrack.default;
		case 'date':
			return lozengeStyleMapping.date.default;
		default:
			return hasScore ? lozengeStyleMapping.default.withScore : lozengeStyleMapping.default.default;
	}
};

const upliftLozengeAppearanceMap: Record<string, SemanticColor> = {
	on_track: 'success',
	at_risk: 'warning',
	off_track: 'danger',
};

const statusAndScoreStyles = xcss({
	display: 'inline-flex',
});

const statusStyles = xcss({
	position: 'relative',
	top: 'space.negative.025',
	left: 'space.050',
	clipPath: 'inset(0 6px 0 0)',
});

const scoreStyles = xcss({
	position: 'relative',
	top: 'space.negative.025',
	right: 'space.050',
	clipPath: 'inset(0 0 0 2px)',
});

const containerStyles = xcss({
	flexShrink: 0,
});

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

const vrStatusDateStyles = xcss({
	font: token('font.body.large'),
	color: 'color.text.subtlest',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

interface AtlasStatusLozengeProps {
	statusValue: string;
	statusLabel: ReactNode;
	statusScore: number | null;
	date: ReactNode;
}

export const AtlasStatusLozenge = ({
	statusValue,
	statusLabel,
	statusScore,
	date,
}: AtlasStatusLozengeProps): React.JSX.Element => {
	const hasScore = statusScore !== null;

	if (fg('platform-dst-lozenge-tag-badge-visual-uplifts')) {
		return (
			<Inline xcss={containerStyles} space="space.100" alignBlock="center">
				<Lozenge
					appearance={upliftLozengeAppearanceMap[statusValue] ?? 'neutral'}
					trailingMetric={hasScore ? String(statusScore) : undefined}
				>
					{statusLabel}
				</Lozenge>
				{date && (
					<>
						<Text size="medium" color="color.text.subtlest" maxLines={1}>
							<FormattedMessage {...messages.for} />
						</Text>
						<Lozenge
							appearance="neutral"
							iconBefore={EditorDateIcon}
							spacing="default"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- transparent date lozenge matches Atlassian Home uplift pattern
							style={{ backgroundColor: 'transparent' }}
						>
							{date}
						</Lozenge>
					</>
				)}
			</Inline>
		);
	}

	const { backgroundColor, secondaryBackgroundColor, color } = getStatusLozengeStyle(
		statusValue,
		hasScore,
	);

	return (
		<Box xcss={statusAndDateContainerStyles}>
			{hasScore ? (
				<Box xcss={statusAndScoreStyles}>
					<Box xcss={statusStyles}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
						<Lozenge style={{ backgroundColor: backgroundColor, color: color }} isBold={true}>
							<Box paddingInlineEnd="space.075">{statusLabel}</Box>
						</Lozenge>
					</Box>
					<Box xcss={scoreStyles}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
						<Lozenge
							style={{ backgroundColor: secondaryBackgroundColor, color: color }}
							isBold={true}
						>
							<Box paddingInlineStart="space.025">{statusScore}</Box>
						</Lozenge>
					</Box>
				</Box>
			) : (
				<>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<Lozenge style={{ backgroundColor: backgroundColor, color: color }} isBold>
						{statusLabel}
					</Lozenge>
				</>
			)}
			{date && (
				<>
					<Box xcss={vrStatusDateStyles}>
						<FormattedMessage {...messages.for} />
					</Box>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<Lozenge style={getStatusLozengeStyle('date', false)}>{date}</Lozenge>
				</>
			)}
		</Box>
	);
};
